import { forwardRef, useId, type SelectHTMLAttributes } from 'react';
import { cn } from '@/utils/cn';
import { Icon } from '../Icon';
import { FormField } from '../FormField/FormField';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface SelectOption {
  /** The value submitted with the form. */
  value: string;
  /** Human-readable label shown in the dropdown. */
  label: string;
  /** Whether this option is disabled. */
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  /** Field label — rendered as a `<label>` above the select via FormField. */
  label?: string;
  /**
   * Error message. When provided:
   * - Activates error state styling (border, background)
   * - Replaces `helperText` in the text slot below the select
   * - Text is rendered in `text-error` colour
   */
  error?: string;
  /**
   * Helper text shown below the select when there is no `error`.
   * Rendered in `text-bodylight` colour.
   */
  helperText?: string;
  /**
   * The options to render inside the select.
   * Use the `SelectOption` shape: `{ value, label, disabled? }`.
   */
  options: SelectOption[];
  /**
   * Optional placeholder option shown when no value is selected.
   * Renders as a disabled, empty-value option at the top of the list.
   */
  placeholder?: string;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const wrapperBase =
  'relative flex items-center rounded-small border bg-surface transition-colors duration-150';

const wrapperDefault = 'border-default focus-within:border-(--border-color-interactiveloud)';

const wrapperError = 'border-error bg-error';

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Primer Select component.
 *
 * A styled wrapper around the native `<select>` element. Shares the same
 * layout, colours, and states as the Primer `Input` component.
 *
 * ### States
 * - **Default** — `border-default`, `bg-surface`, `text-body`
 * - **Error** — `border-error`, `bg-error`; sub-text slot shows `error` in `text-error`
 *
 * ### Layout stability
 * The sub-text row is always present in the DOM (via `FormField`) so the field
 * never changes height when transitioning into or out of error state.
 *
 * ### Chevron
 * The native select arrow is hidden (`appearance-none`) and replaced with
 * `chevron-up` / `chevron-down` icons stacked on the right side of the field.
 * The chevron pair is purely decorative (`aria-hidden`).
 *
 * ### Accessibility
 * Label association and id management are handled by `FormField` / `useId`.
 *
 * @example
 * // Basic
 * <Select options={[{ value: 'au', label: 'Australia' }]} label="Country" />
 *
 * @example
 * // With placeholder
 * <Select options={options} label="Country" placeholder="Select a country…" />
 *
 * @example
 * // Error state
 * <Select options={options} label="Country" error="Please select a country" />
 *
 * @example
 * // With helper text
 * <Select options={options} label="Country" helperText="Used for shipping calculations" />
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, error, helperText, options, placeholder, id, className = '', ...props },
  ref
) {
  const generatedId = useId();
  const selectId = id ?? generatedId;

  const hasError = Boolean(error);

  return (
    <FormField label={label} error={error} helperText={helperText} inputId={selectId}>
      {/* ── Select wrapper ──────────────────────────────────────────────
       * The visual border/background lives on this wrapper div.
       * The native <select> is transparent, fills all space, and sits
       * beneath the chevron icons (z-index ensures it remains clickable).
       */}
      <div
        data-select-wrapper=""
        className={cn(wrapperBase, hasError ? wrapperError : wrapperDefault, className)}
      >
        {/* ── Native select ───────────────────────────────────────────── */}
        <select
          ref={ref}
          id={selectId}
          className={cn(
            // Typography
            'body-small',
            // Layout — fill all wrapper space; sit above the chevron overlay
            'relative z-10 w-full appearance-none',
            // Spacing — matches Input padding
            'px-4 py-4',
            // Transparent so wrapper bg shows through
            'bg-transparent',
            // Text colours
            hasError ? 'text-error' : 'text-body',
            // Remove native focus ring
            'outline-none',
            // Disabled
            'disabled:cursor-not-allowed disabled:text-disabled',
            // Right padding to prevent text from overlapping chevrons
            'pr-10'
          )}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>

        {/* ── Chevron icon (right side, decorative) ───────────────────── */}
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute right-4 flex items-center',
            // Colour tracks the select text state
            hasError ? 'text-error' : 'text-body',
            props.disabled && 'text-disabled'
          )}
        >
          <Icon name="chevron-down" size="large" />
        </span>
      </div>
    </FormField>
  );
});

export default Select;
